import React, { useEffect, useState } from 'react';
import { Button, Space, Input, Form, Select, Row, Col, Tag, Typography, notification } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined, SyncOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { AppSelect } from '../../../constants/appSelect';
import { newObjectKeys } from '../../../utils/utility';
import {
  deleteTagVideoRequest,
  getListTagVideosRequest,
  updateTagPositionRequest,
} from '../../../services/lichvietvideo/tagVideoServiceRequest';
import isEqual from 'lodash/isEqual';
import TableSortPlaceCustom from '../../../components/TableSortPlaceCustom';
import { min } from 'lodash';

const { Text } = Typography;

const QuanLyTagVideo = () => {
  const navigate = useNavigate();
  const [formSearch] = Form.useForm();
  const [params, setParams] = useState({});
  const [dataTagPosition, setDataTagPosition] = useState([]);

  const {
    data: dataTagVideos,
    isPending,
    refetch: refetchDataTags,
  } = useQuery({ queryKey: ['dataTagVideos', params], queryFn: () => getListTagVideosRequest(params) });

  useEffect(() => {
    if (dataTagVideos?.data) {
      setDataTagPosition(dataTagVideos?.data);
    }
  }, [dataTagVideos]);

  const { mutate: mutateDeleteTagVideo } = useMutation({
    mutationFn: deleteTagVideoRequest,
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({
          message: 'Xóa video thành công!',
        });
        refetchDataTags();
      } else {
        notification.error({
          message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
      });
    },
  });

  const { mutate: mutateUpdateTagPosition, isPending: isPendingUpdateTagPosition } = useMutation({
    mutationFn: updateTagPositionRequest,
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({
          message: 'Cập nhật ví trí tag thành công!',
        });
        refetchDataTags();
      } else {
        notification.error({
          message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
      });
    },
  });

  const handleSubmitForm = (values) => {
    const temp = {};
    if (values?.name) {
      Object.assign(temp, { name: values?.name });
    }
    if (values?.description) {
      Object.assign(temp, { description: values?.description });
    }
    if (values?.status >= 0) {
      Object.assign(temp, { status: [values?.status] });
    }
    if (!isEqual(temp, params)) {
      setParams(temp);
      return;
    }
    refetchDataTags();
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      fixed: 'left',
    },
    {
      title: 'Tên tag video',
      dataIndex: ['id', 'name'],
      key: 'id',
      render: (_, { name, id }) => (
        <Link to={`/lv-video/quan-ly-tag-video/${id}/sua-tag-video`} className="text-[#1677FF]">
          {name}
        </Link>
      ),
      minWidth: 150,
      width: 200,
      fixed: 'left',
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
      render: (description) => <Text >{description}</Text>,
      minWidth: 250,
      width: 400,
    },
    {
      title: 'Thời gian tạo',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (_, { created_at }) => (
        <Space direction="vertical">{created_at && <Text className="text-[#667085]">{created_at}</Text>}</Space>
      ),
      minWidth: 120,
      width: 120,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag className="px-2 py-1" color={AppSelect.STATUS?.find((item) => item?.value === status)?.color}>
          {AppSelect.STATUS?.find((item) => item?.value === status)?.label}
        </Tag>
      ),
      width: 120,
      fixed: 'right',
    },
    {
      title: 'Thứ tự',
      dataIndex: 'sort_place',
      key: 'sort_place',
      width: 80,
      fixed: 'right',
    },
    {
      title: 'Thao tác',
      key: 'action',
      render: (_, record) => (
        <Space size={0}>
          <Button type="text" onClick={() => navigate(`/lv-video/quan-ly-tag-video/${record?.id}/sua-tag-video`)}>
            <EditOutlined />
          </Button>
          <Button type="text" onClick={() => mutateDeleteTagVideo({ id: record?.id })}>
            <DeleteOutlined />
          </Button>
        </Space>
      ),
      width: 90,
      fixed: 'right',
    },
  ];

  return (
    <div className="m-5">
      <div className="flex justify-between items-center mb-5">
        <h1 className="text-2xl">Danh sách tag video</h1>
        <div className="flex justify-end gap-2">
          <Button
            type="default"
            loading={isPendingUpdateTagPosition}
            onClick={() => mutateUpdateTagPosition({ ids: dataTagPosition?.map((item) => item?.id) })}
          >
            <SyncOutlined />
            Cập nhật vị trí
          </Button>
          <Link to={'/lv-video/quan-ly-tag-video/them-tag-video'}>
            <Button type="primary">
              <PlusOutlined />
              Thêm tag video
            </Button>
          </Link>
        </div>
      </div>

      <Form
        name="search_tag_video"
        form={formSearch}
        layout="vertical"
        requiredMark={false}
        className="mb-5"
        onFinish={handleSubmitForm}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="name" label={<Text>Tên tag video</Text>}>
              <Input placeholder="Nhập tên tag video..." />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="description" label={<Text>Mô tả</Text>}>
              <Input placeholder="Nhập mô tả..." />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="status" label={<Text>Trạng thái</Text>} initialValue={-1}>
              <Select
                showSearch
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={[{ value: -1, label: 'Tất cả' }, ...AppSelect.STATUS]}
              />
            </Form.Item>
          </Col>
          <Col className="flex items-center gap-2">
            <Button type="primary" htmlType="submit">
              <SearchOutlined />
              Tìm kiếm
            </Button>
            {params &&
              Object.values(newObjectKeys(params, ['name', 'description', 'status'])).some((value) => value) && (
                <Button
                  htmlType="button"
                  onClick={() => {
                    formSearch.resetFields();
                    setParams({});
                  }}
                >
                  <DeleteOutlined />
                  Xóa lọc
                </Button>
              )}
          </Col>
        </Row>
      </Form>

      <TableSortPlaceCustom columns={columns} dataSource={dataTagPosition} setDataSource={setDataTagPosition} />
    </div>
  );
};

export default QuanLyTagVideo;
